import { useCallback, useEffect, useState } from "react";
import type { ThemePreference } from "../api/types.js";
import { initialThemePreference, THEME_STORAGE_KEY } from "./theme-preference.js";

function storedTheme(): ThemePreference {
  return initialThemePreference(window.localStorage);
}

function resolveTheme(
  preference: ThemePreference,
  dark: boolean,
): Exclude<ThemePreference, "system"> {
  return preference === "system" ? (dark ? "vantablack" : "light") : preference;
}

export function useTheme(): {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(storedTheme);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      document.documentElement.setAttribute("data-theme", resolveTheme(preference, media.matches));
      document.documentElement.setAttribute("data-theme-preference", preference);
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // The in-memory preference still applies when storage is unavailable.
    }
  }, []);

  return { preference, setPreference };
}
